import React, { useState, useEffect, useRef } from 'react';
import { Bars3Icon, BellIcon, MagnifyingGlassIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate } from 'react-router-dom';
import { userAPI } from '../../services/api';
import BrandLogo from '../common/BrandLogo';
import Avatar from '../common/Avatar';
import { UserSuggestion } from '../../types';
import { fetchUnreadMessageCount } from '../../store/slices/messagesSlice';
import { fetchUnreadCount } from '../../store/slices/notificationsSlice';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';

interface TopHeaderProps {
  onMenuClick: () => void;
  title?: string;
}

const TopHeader: React.FC<TopHeaderProps> = ({ onMenuClick, title = 'ClickCollab' }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { unreadCount: unreadMessageCount } = useAppSelector((state) => state.messages);
  const { unreadCount: unreadNotificationCount } = useAppSelector((state) => state.notifications);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<UserSuggestion[]>([]);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(fetchUnreadMessageCount());
    dispatch(fetchUnreadCount());
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (query.length >= 2) {
      searchTimeout.current = setTimeout(async () => {
        try {
          const response = await userAPI.searchUsers(query);
          const rawSuggestions: UserSuggestion[] = response.data.data || [];
          const activeSuggestions = rawSuggestions.filter(
            (u) => !u.is_disabled && !u.deletion_scheduled_at && !u.deleted_at
          );
          setSuggestions(activeSuggestions);
          setShowSuggestions(true);
        } catch (error) {
          console.error('Search failed:', error);
          setSuggestions([]);
        }
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/explore?search=${encodeURIComponent(searchQuery)}`);
      setShowSuggestions(false);
    }
  };

  const handleSelectUser = (user: UserSuggestion) => {
    navigate(`/profile/${user.id}`);
    setSearchQuery('');
    setShowSuggestions(false);
  };

  return (
    <header 
      className="fixed top-0 left-0 right-0 md:left-64 h-auto bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 z-[60] flex items-center justify-between px-4 md:px-8 transition-all duration-300"
      style={{
        paddingTop: 'calc(max(var(--safe-area-inset-top, 0px), env(safe-area-inset-top, 0px)) + 0.625rem)',
        paddingBottom: '0.625rem',
        minHeight: 'calc(4rem + max(var(--safe-area-inset-top, 0px), env(safe-area-inset-top, 0px)))'
      }}
    >
      {/* Mobile Search Overlay */}
      {showMobileSearch && (
        <div 
          className="fixed inset-0 bg-white dark:bg-gray-900 z-[60] flex flex-col px-4 pb-2 md:hidden"
          style={{
            paddingTop: 'calc(max(var(--safe-area-inset-top, 0px), env(safe-area-inset-top, 0px)) + 0.75rem)'
          }}
        >
          <div className="flex items-center gap-3 mb-4">
            <form onSubmit={handleSearchSubmit} className="flex-1 flex items-center bg-gray-50 dark:bg-gray-800 rounded-full px-4 py-2 border border-gray-200 dark:border-gray-700">
              <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 mr-2" />
              <input 
                type="text" 
                value={searchQuery}
                onChange={handleSearchChange}
                autoFocus
                placeholder="Search..." 
                className="bg-transparent border-none focus:ring-0 focus:outline-none text-sm w-full text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
            </form>
            <button 
              onClick={() => setShowMobileSearch(false)}
              className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-full outline-none focus:outline-none"
            >
              <span className="text-sm font-medium">Cancel</span>
            </button>
          </div>

          {/* Mobile Search Suggestions */}
          {suggestions.length > 0 && (
            <div className="flex-1 overflow-y-auto">
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-2 py-2 uppercase tracking-wider">
                People
              </div>
              {suggestions.map((user) => (
                <button
                  key={user.id}
                  onClick={() => {
                    handleSelectUser(user);
                    setShowMobileSearch(false);
                  }}
                  className="w-full flex items-center gap-3 px-2 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors text-left border-b border-gray-50 dark:border-gray-800 last:border-0 outline-none focus:outline-none"
                >
                  <Avatar 
                    src={user.profile_pic_signed || user.profile_pic} 
                    alt={user.full_name} 
                    size="sm" 
                    className="flex-shrink-0"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{user.full_name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user.role}</p>
                  </div>
                </button>
              ))}
              <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                <button
                  onClick={(e) => {
                    handleSearchSubmit(e);
                    setShowMobileSearch(false);
                  }}
                  className="w-full text-left px-2 py-3 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg font-medium transition-colors outline-none focus:outline-none"
                >
                  See all results for "{searchQuery}"
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Left: Hamburger (Mobile) & Title/Search */}
      <div className="flex items-center gap-4">
        <button
          onClick={onMenuClick}
          className="p-2 -ml-2 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors md:hidden"
          aria-label="Open menu"
        >
          <Bars3Icon className="w-6 h-6" />
        </button>
        
        {/* Mobile Title */}
        <div className={`md:hidden ${showMobileSearch ? 'opacity-0' : 'opacity-100'}`}>
          <Link to="/home">
            <BrandLogo textSize="text-lg" />
          </Link>
        </div>
      </div>

      {/* Universal Search Bar (Visible on Desktop) - Centered */}
      <div className="hidden md:flex flex-1 justify-center px-8 relative" ref={wrapperRef}>
        <div className="w-full max-w-md relative">
          <form onSubmit={handleSearchSubmit} className="flex items-center w-full bg-gray-50 dark:bg-gray-800 rounded-full px-4 py-2 border border-gray-200 dark:border-gray-700 transition-all">
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 mr-2" />
            <input 
              type="text" 
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => searchQuery.length >= 2 && setShowSuggestions(true)}
              placeholder="Search jobs, creators, or inspiration..." 
              className="bg-transparent border-none focus:ring-0 focus:outline-none text-sm w-full text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
          </form>

          {/* Search Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 max-h-96 overflow-y-auto z-[9999]">
              <div className="p-2">
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-3 py-2 uppercase tracking-wider">
                  People
                </div>
                {suggestions.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => handleSelectUser(user)}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors text-left"
                  >
                    <Avatar 
                      src={user.profile_pic_signed || user.profile_pic} 
                      alt={user.full_name} 
                      size="sm" 
                      className="flex-shrink-0"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{user.full_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user.role}</p>
                    </div>
                  </button>
                ))}
              </div>
              <div className="border-t border-gray-100 dark:border-gray-700 p-2">
                <button
                  onClick={handleSearchSubmit}
                  className="w-full text-left px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg font-medium transition-colors"
                >
                  See all results for "{searchQuery}"
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Right: Actions */}
      <div className="flex items-center gap-2 md:gap-4">
        {/* Mobile Search Icon */}
        <button 
          onClick={() => setShowMobileSearch(true)}
          className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-full md:hidden outline-none focus:outline-none"
        >
             <MagnifyingGlassIcon className="w-6 h-6" />
        </button>

        <Link to="/messages" className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-full transition-colors relative">
          <ChatBubbleLeftRightIcon className="w-6 h-6" />
          {unreadMessageCount > 0 && (
            <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] flex items-center justify-center bg-blue-500 text-white text-[10px] font-bold rounded-full border-2 border-white dark:border-gray-900 px-1">
              {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
            </span>
          )}
        </Link>

        <Link to="/notifications" className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-full transition-colors relative">
          <BellIcon className="w-6 h-6" />
          {unreadNotificationCount > 0 && (
            <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full border-2 border-white dark:border-gray-900 px-1">
              {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
            </span>
          )}
        </Link>

        {/* Profile Link */}
        <Link to={`/profile/${useAppSelector((state) => state.auth.user?.id)}`} className="ml-2">
          <Avatar 
            src={useAppSelector((state) => state.auth.user?.profile_pic_signed || state.auth.user?.profile_pic)} 
            alt={`${useAppSelector((state) => state.auth.user?.first_name || '')} ${useAppSelector((state) => state.auth.user?.last_name || '')}`} 
            size="sm" 
            className="w-8 h-8 ring-2 ring-transparent hover:ring-gray-200 dark:hover:ring-gray-700 transition-all"
          />
        </Link>
      </div>
    </header>
  );
};

export default TopHeader;
